import APIS from 'src/constants/ApiConfig';
import HttpService from './HttpService';
import { IPageResult } from '../entity/BaseResult';
import { ITranslate } from 'src/entity/BasicEntity';
import I18N from 'src/services/I18NService';
import { mockUrl, mock } from 'src/constants/Mock';
import { EnumMedicineType } from 'src/constants/enum';

const useMock = false;

export const getMallUrl = (url: string) => {
  if (useMock) {
    return mockUrl(mock.mall, url);
  } else {
    return APIS.domainApi(url);
  }
};

/**
 * 改价申请记录状态枚举
 *
 * @export
 * @enum {number}
 */
export enum EnumAdjustStatus {
  /** 审批中 */
  APPROVING = 'APPROVING',
  /** 已审批 */
  APPROVED = 'APPROVED',
  /** 草稿 */
  ONLY_FOR_RECORD = 'ONLY_FOR_RECORD',
}

/**
 * 改价申请记录状态列表
 */
export const AdjustStatusList: ITranslate[] = [
  {
    key: 'common_status_panding',
    value: EnumAdjustStatus.APPROVING,
  },
  {
    key: 'common_status_approved',
    value: EnumAdjustStatus.APPROVED,
  },
];

/**
 * 改价申请明细枚举
 *
 * @export
 * @enum {number}
 */
export enum EnumApproveStatus {
  /** 待审批 */
  WAITING_APPROVE = 'WAITING_APPROVE',
  /** 审批通过 */
  APPROVE_PASSED = 'APPROVE_PASSED',
  /** 审批驳回 */
  APPROVE_REJECTED = 'APPROVE_REJECTED',
}

/**
 * 审批价格的类型
 *
 */
export enum EnumPriceType {
  /** 供货价 */
  SUPPLY_PRICE = 'SUPPLY_PRICE',
  /** 底价 */
  FLOOR_PRICE = 'FLOOR_PRICE',
}

/**
 * 改价申请明细状态列表
 */
export const ApproveStatusList: ITranslate[] = [
  {
    key: 'common_status_panding',
    value: EnumApproveStatus.WAITING_APPROVE,
  },
  {
    key: 'common_status_passed',
    value: EnumApproveStatus.APPROVE_PASSED,
  },
  {
    key: 'common_status_dismiss',
    value: EnumApproveStatus.APPROVE_REJECTED,
  },
];

/**
 * 改价申请后台接受数据
 *
 * @export
 * @interface IPriceChangeParam
 */
export interface IPriceChangeParam {
  /**
   * 调价申请SKUUUID
   *
   * @type {string}
   */
  sku: string;
  /**
   * 调价申请详情
   *
   * @type {Array<IPriceChangeDetail>}
   */
  details: IPriceChangeDetail[];
}

/**
 * SKU改价状态数据
 *
 * @exports
 * @interface IPriceApprove
 */
export interface IPriceApprove {
  /**
   * SKU信息
   *
   * @type {IPriceChangeSKU}
   */
  medicine: IPriceChangeSKU;
  /**
   * SKU 改价状态
   *
   * @type {IPriceChangeItem}
   */
  item: IPriceChangeItem;
  /**
   *  调整前供货价
   *
   * @type {number}
   * @member IPriceApprove
   */
  original_supply_price?: number;
  /**
   *  调整后供货价
   *
   * @type {number}
   * @member IPriceApprove
   */
  new_supply_price?: number;
  /**
   *  调整前底价
   *
   * @type {number}
   * @member IPriceApprove
   */
  original_floor_price?: number;
  /**
   *  调整后底价
   *
   * @type {number}
   * @member IPriceApprove
   */
  new_floor_price?: number;
  /**
   * 导入数据是否进行价格审批
   *
   * @type {boolean}
   * @member IPriceApprove
   */
  is_approving?: boolean;
  /** 扩展字段 每条记录的key,非后台返回 */
  rowKey?: string;
}

/**
 * 调价详情数据
 *
 * @exports
 * @interface IPriceChangeDetail
 */
export interface IPriceChangeDetail {
  /**
   * 申请调价类型
   *
   * @type {EnumPriceType}
   */
  price_type: EnumPriceType;
  /**
   * 调价前的价格
   *
   * @type {number}
   */
  original_price: number;
  /**
   * 调价的价格
   *
   * @type {number}
   */
  new_price: number;
}
/**
 * 改价申请记录数据
 *
 * @export
 * @interface IPriceChangeData
 */
export interface IPriceChangeData {
  /**
   * 调价申请记录UUID
   *
   * @type {string}
   * @memberof IPriceChangeData
   */
  uuid: string;
  /**
   * 配药中心ID
   *
   * @type {string}
   * @memberof IPriceChangeData
   */
  seller_user: string;
  /**
   * 配药中心名称
   * @type {string}
   * @memberof IPriceChangeData
   */
  seller_user_name: string;
  /**
   * 审批状态
   * -1, '草稿'
   * 0, '待审批'
   * 1, '已审批'
   * @type {EnumAdjustStatus}
   * @memberof IPriceChangeData
   */
  status: EnumAdjustStatus;
  /**
   * 申请人
   *
   * @type {string}
   * @memberof IPriceChangeData
   */
  applicant_name: string;
  applicant_uuid: string;
  /**
   * 申请时间
   *
   * @type {string}
   * @memberof IPriceChangeData
   */
  application_date: string;
  /**
   * 审批人
   *
   * @type {string}
   * @memberof IPriceChangeData
   */
  approve_uuid: string;
  approve_name: string;
  /**
   * 申请项目
   *
   * @type {IPriceChangeItem[]}
   * @memberof IPriceChangeData
   */
  item_uuid_list: string[];
}

/**
 * 改价申请明细数据
 *
 * @export
 * @interface IPriceChangeItem
 */
export interface IPriceChangeItem {
  /**
   * 调价申请明细表的UUID
   * 如果为null,表示后台未创建记录，new_price显示未“前次供货价调整中”
   * 如果未数值，表示后台创建记录，提交申请时，需要修改new_price
   * @type {string}
   * @memberof IPriceChangeItem
   */
  uuid?: string;
  /**
   * 药品对象
   *
   * @type {IPriceChangeSKU}
   * @memberof IPriceChangeItem
   */
  medicine: IPriceChangeSKU;
  /**
   * SKUUUID
   *
   * @type {string}
   * @memberof IPriceChangeItem
   */
  sku: string;
  /**
   * 调整前的价格
   *
   * @type {string}
   * @memberof IPriceChangeItem
   */
  original_price?: number;
  /**
   * 调整后的价格
   *
   * @type {number}
   * @memberof IPriceChangeItem
   */
  new_price?: number;
  /**
   * 审批人
   *
   * @type {string}
   * @memberof IPriceChangeItem
   */
  approve_uuid: string;
  approve_name: string;
  /**
   * 审批状态
   *
   * @type {EnumApproveStatus}
   * @memberof IPriceChangeItem
   */
  status: EnumApproveStatus;
  /** 扩展字段 每条记录的key,非后台返回 */
  rowKey: string;
  /**
   * 审批详情
   *
   * @type {IPriceChangeDetail}
   * @memberof IPriceChangeItem
   */
  details: IPriceChangeDetail[];
}

interface IPriceChangeSKU {
  /** UUID */
  uuid: string;
  /** 药品名称 */
  name: string;
  /** 药品简称 */
  short_name: string;
  /** 脉景标准中药 */
  spu: string;
  /** 品牌 */
  brand: string;
  /** 药库 */
  medicine_group: string;
  /** 拼音 */
  pinyin: string;
  /** 药品编码，药品ID */
  code: string;
  /** 商家药品编码 */
  medicine_code: string;
  /** 药库剂型，0饮片 1颗粒 2超微中药 10中成药 */
  dosage_form: EnumMedicineType;
  /** 生产商 */
  manufacturer: string;
  /** 产地 */
  production_place: string;
  /** 供应价 */
  supply_price: number;
  /** 底价 */
  floor_price: number;
  /** 规格 */
  specification: number;
  /** 零售单位 */
  retail_unit: string;
  /** 服用单位 */
  take_unit: string;
  /** 起订量 */
  min_order_quantity: number;
  /** 订购单位 */
  order_unit: number;
  /** 药效当量 */
  equivalent: number;
  /** 单次用量 */
  single_dose: number;
  /** 每日服用次数 */
  daily_take_times: number;
  /** 中成药介绍 */
  description: string;
  /** 上架/下架 */
  is_active: boolean;
  /** 缺货状态,true有货，false缺货 */
  in_stock: boolean;
  /** 删除状态，true删除，false未删除 */
  is_deleted: boolean;
  // /** 创建时间 */
  // created_time: string;
  // /** 修改时间 */
  // modified_time: string;
}

/**
 * 西成药批量改价数据
 *
 * @export
 * @interface IPriceChangeData
 */
export interface IWestPriceChangeData {
  sku: string;
  supply_price: number;
  floor_price: number;
}

/**
 * 价格调整服务
 *
 * @class PriceChangeService
 */
class PriceChangeService {
  /**
   * 生成药品供货价调整申请数据
   *
   * @memberof PriceChangeService
   */
  getPriceChangeDetailData = (UUIDList: string[]): Promise<IPriceApprove[] | undefined> => {
    const url = getMallUrl(APIS.priceChange.priceWaitingApprove);
    return HttpService.get(url, {
      uuid__in: UUIDList.join(),
    });
  };

  /**
   * 根据改价申请记录id获取详情
   *
   * @memberof PriceChangeService
   */
  getPriceChangeDataByUUID = (UUID: string): Promise<IPriceChangeData | undefined> => {
    const url = `${getMallUrl(APIS.priceChange.priceAdjustmentApplication)}${UUID}/`;
    // const url = 'https://yapi.macrocura.com/mock/84/api/v1/medicine/supply-price/adjustment/';
    return HttpService.get(url);
  };

  /**
   * 获取改价申请记录药品信息
   *
   * @memberof PriceChangeService
   */
  getPriceChangeDataByUUIDS = (uuidList: string[]): Promise<IPriceChangeItem[]> => {
    const url = `${getMallUrl(APIS.priceChange.priceAdjustmentItem)}`;
    return HttpService.post(url, {
      uuids: uuidList,
    });
  };

  /** 根据导入接口返回的 adjustment 字段获取该次导入药品需要价格审核的数据 */
  getPriceNeedChangeByUUID = (UUID: string): Promise<IPriceApprove[]> => {
    const url = `${getMallUrl(APIS.priceChange.priceAdjustmentApplication)}${UUID}/by-import/`;
    return HttpService.get(url);
  };

  /**
   * 获取改价申请记录
   *
   * @memberof PriceChangeService
   */
  getPriceChangeDataList = (offset: number, limit: number): Promise<IPageResult<IPriceChangeData[]>> => {
    const url = getMallUrl(APIS.priceChange.priceAdjustmentApplication);
    // const url = 'https://yapi.macrocura.com/mock/84/api/v1/medicine/supply-price/adjustment/';
    return HttpService.get(url, {
      offset,
      limit,
    });
  };

  /**
   * 药品供货价调整申请提交
   *
   * @memberof PriceChangeService
   */
  priceChangeFloorSupply = (approveList: IPriceChangeParam[]) => {
    const url = getMallUrl(`${APIS.priceChange.priceAdjustmentApplication}`);
    return HttpService.post(url, { items: approveList });
  };

  /**
   * 西成药药品改价
   * @param approveList
   */
  priceChangeWestFloorSupply = (changeList: IWestPriceChangeData[]) => {
    const url = getMallUrl(`${APIS.mall.westernSkuUpdatePrice}`);
    return HttpService.put(url, changeList);
  };

  /**
   * 获取改价申请记录审核状态描述
   *
   * @memberof PriceChangeService
   */
  getAdjustStatusDesc = (value: EnumAdjustStatus): string => {
    const obj = AdjustStatusList.find((t) => t.value === value);
    if (!!obj) {
      return I18N.get(obj.key);
    }
    return '';
  };

  /**
   * 获取改价申请明细审核状态描述
   *
   * @memberof PriceChangeService
   */
  getApproveStatusDesc = (value: EnumApproveStatus): string => {
    const obj = ApproveStatusList.find((t) => t.value === value);
    if (!!obj) {
      return I18N.get(obj.key);
    }
    return '';
  };

  /**
   * 通过药品SKU UUID检测是否有改价申请记录
   *
   * @memberof PriceChangeService
   */
  hasChangePriceApply = async (uuid: string): Promise<boolean> => {
    try {
      const recordData = await this.getPriceChangeDetailData([uuid]);
      if (!!recordData && !recordData[0].item) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  /**
   * 通过接口发送改价申请
   *
   * @memberof PriceChangeService
   */
  autoChangePrice = async (priceChangeDetail: IPriceChangeParam): Promise<boolean> => {
    try {
      const recordData = await this.getPriceChangeDetailData([priceChangeDetail.sku]);
      if (!!recordData && recordData.length === 1) {
        if (!recordData[0].item) {
          await this.priceChangeFloorSupply([priceChangeDetail]);
          return true;
        } else {
          return false;
        }
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };
}

export default new PriceChangeService();



// WEBPACK FOOTER //
// ./src/services/PriceChangeService.ts